import axios from "axios";
import { encodeData } from "../../../helper/common/encodeData";

export const BACKEND_SEARCH_LIMIT = 500;
export const BACKEND_SEARCH_DEBOUNCE_MS = 400;

export function sortOptionsAlphabetically(options = []) {
  return [...(options || [])].sort((a, b) =>
    String(a?.label ?? a?.value ?? "").localeCompare(
      String(b?.label ?? b?.value ?? ""),
      undefined,
      { sensitivity: "base" }
    )
  );
}

export function stripHtml(htmlString) {
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  return doc.body.textContent || "";
}

function buildNameContainsFiltering(searchTerm = "") {
  const term = String(searchTerm ?? "").trim();
  if (!term) return "";
  return encodeData([
    { field_name: "name", operator: "CONTAINS", value: term },
  ]);
}

/**
 * Fetch dropdown options for ComplexFilter fields.
 * @param {string} apiKey
 * @param {string} clientId
 * @param {{ searchTerm?: string, limit?: number, backendSearch?: boolean }} [options]
 *   When backendSearch is true, uses smaller limit and optional CONTAINS filtering.
 */
export const fetchOptionsFromApi = async (apiKey, clientId, options = {}) => {
  const { searchTerm = "", backendSearch = false } = options;
  const limit = options.limit ?? (backendSearch ? BACKEND_SEARCH_LIMIT : undefined);
  const filtering = backendSearch ? buildNameContainsFiltering(searchTerm) : "";

  let config = {
    method: "get",
    maxBodyLength: Infinity,
    url: "",
    headers: {},
  };

  if (apiKey === "portfolio_names_api_key") {
    const queryLimit = limit ?? 10000;
    const params = [
      "source=amazon",
      "fields=name,platform_id",
      `limit=${queryLimit}`,
    ];
    if (filtering) params.push(`filtering=${filtering}`);
    config.url = `${process.env.REACT_APP_ADS_API}/client/${clientId}/portfolios?${params.join("&")}`;
    const response = await axios.request(config);
    return (response.data.data || []).map((item) => ({
      value: item.name,
      label: item.name,
    }));
  }

  if (apiKey === "campaign_names_api_key") {
    const queryLimit = limit ?? 100000;
    const params = [
      "source=amazon",
      "fields=name,status",
      `limit=${queryLimit}`,
    ];
    if (backendSearch) {
      params.push("order_by=daily_budget", "sorting_order=DESC");
    }
    if (filtering) params.push(`filtering=${filtering}`);
    config.url = `${process.env.REACT_APP_ADS_API}/client/${clientId}/campaigns?${params.join("&")}`;
    const response = await axios.request(config);
    return (response.data.data || []).map((item) => ({
      value: item.name,
      label: item.name,
      status: item.status,
    }));
  }

  if (apiKey === "ad_group_names_api_key") {
    const queryLimit = limit ?? (backendSearch ? BACKEND_SEARCH_LIMIT : 10000);
    const params = [
      "source=amazon",
      "fields=id,name,platform_id,campaign_id,status",
      `limit=${queryLimit}`,
    ];
    if (filtering) params.push(`filtering=${filtering}`);
    config.url = `${process.env.REACT_APP_ADS_API}/client/${clientId}/adsets?${params.join("&")}`;
    const response = await axios.request(config);
    return (response.data.data || []).map((item) => ({
      value: item.name,
      label: item.name,
      status: item.status,
    }));
  }

  if (apiKey === "category_names_api_key") {
    config.url = `${process.env.REACT_APP_CATALOG_API}/client/${clientId}/categories?source=amazon&fields=name`;
    const response = await axios.request(config);
    return response.data.data.map((item) => {
      return { value: item.name, label: item.name };
    });
  }

  if (apiKey === "product_set_api_key") {
    const queryLimit = limit ?? (backendSearch ? BACKEND_SEARCH_LIMIT : undefined);
    const params = ["source=amazon"];
    if (queryLimit) params.push(`limit=${queryLimit}`);
    if (backendSearch) {
      const term = String(searchTerm ?? "").trim();
      if (term) {
        params.push(
          `filtering=${encodeData([
            { field_name: "product_set", operator: "CONTAINS", value: term },
          ])}`
        );
      }
    }
    config.url = `${process.env.REACT_APP_ADS_API}/client/${clientId}:product-set?${params.join("&")}`;
    const response = await axios.request(config);
    return (response.data.data || [])
      .map((item) => {
        return { value: item?.product_set, label: item?.product_set };
      })
      .filter((item) => item?.value); // Filter out any undefined values
  }

  return []; // Return empty array if no API key matches
};

const processField = async (field, clientId, categories) => {
  if (field?.dynamicOptions && !field.optionsType && !field?.dataFromApi) {
    let data = categories[field.optionsKey] ?? [];
    return { ...field, optionData: sortOptionsAlphabetically(data) };
  } else if (field?.dataFromApi === true) {
    // Backend-search fields load on demand in ComplexFilter — skip bulk preload.
    if (field?.backendSearch === true) {
      return { ...field, optionData: [] };
    }
    try {
      let data = await fetchOptionsFromApi(field?.apiKey, clientId);
      return { ...field, optionData: sortOptionsAlphabetically(data) };
    } catch (error) {
      console.error(error);
      return { ...field, optionData: [] }; // Return field with empty options in case of error
    }
  } else if (field?.optionsType === "Static" && field?.optionsKey) {
    // This assumes that the static values are stored in `field.optionsKey` as a comma-separated string.
    const staticValues = field.optionsKey.split(",");
    const optionData = staticValues.map((value) => ({ value, label: value }));
    return { ...field, optionData: sortOptionsAlphabetically(optionData) };
  } else {
    return { ...field, optionData: [] };
  }
};

export const processFields = async (fields, clientId, categories) => {
  const promises = fields?.map((field) =>
    processField(field, clientId, categories)
  );
  const results = await Promise.all(promises);
  return results;
};
